import { isCvImportReviewCancelled, isCvImportSourceChanged } from '../../lib/cv-import-review';
/**
 * Everything the resume composer does that is not markup.
 *
 * `ResumeImportComposer.tsx` and `.web.tsx` used to carry byte-for-byte copies
 * of this: the draft state, the text validation, the parse call, the
 * 2026-09-02 recovery branch, and every analytics event. Two copies of a
 * failure path is one copy too many, and it is also why "attaching a file is
 * enough" had to be implemented twice. It lives here once, and the platforms
 * bring their own file type, their own parse call and their own markup.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CvParseQuality, ParsedCvData } from '@tabbio/types';
import {
  captureProductException,
  getErrorObservabilityProperties,
  trackProductEvent,
} from '../../lib/product-analytics';
import {
  getCvParseRecovery,
  getResumeTextValidationError,
  isExpectedOnboardingApiError,
  isUnreadableResumeFileError,
  OnboardingApiError,
} from '../../lib/onboarding-api';
import {
  resolveCvParseRecoveryMessage,
  resolveImportedCvIncompleteMessage,
} from '../../lib/cv-parse-recovery';

export type ResumeImportComposerParsed = {
  parsed: ParsedCvData;
  source: 'file' | 'text';
  /**
   * How the server parsed the document. `fallbackUsed` means the exact
   * extracted text was used without AI; callers show a review step before
   * writing it over the profile.
   */
  quality?: CvParseQuality;
  importEvidenceId?: string;
};

/** What the composer draft looks like from the outside. */
export interface ResumeComposerDraft {
  hasText: boolean;
  hasFile: boolean;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface UseResumeImportComposerOptions<TFile> {
  t: Translate;
  parse: (input: {
    file: TFile | null;
    text: string;
  }) => Promise<{ parsed?: ParsedCvData | null; quality?: CvParseQuality; importEvidenceId?: string } | null>;
  /** Mime type and size of an attached file, for the analytics payload. */
  describeFile: (file: TFile) => { type: string; sizeKb: number | null };
  onParsed: (result: ResumeImportComposerParsed) => void | Promise<void>;
  parseFailedMessage: string;
  analyticsContext?: string;
  /** Platform preamble - haptics natively, and the iOS keyboard dismiss. */
  onBeforeParse?: (source: 'file' | 'text') => void | Promise<void>;
  /** Called when the recovery path clears a file the caller also tracks. */
  onFileCleared?: () => void;
  /** True while the caller is doing something that must block the composer. */
  disabled?: boolean;
  /**
   * Start reading the moment a file is attached, instead of waiting for the
   * send arrow. Opt-in: on a surface whose real submit is a Continue button,
   * a file sitting unread behind a second button is a dead end.
   */
  parseOnAttach?: boolean;
  /**
   * The host is not ready for a read yet (it is still loading, or failed to
   * load, what the read would replace): the input shows, but nothing can be
   * sent. Unlike `disabled`, it shows no wait, because nothing is being read.
   */
  locked?: boolean;
  onBusyChange?: (busy: boolean) => void;
  onDraftChange?: (draft: ResumeComposerDraft) => void;
}

/**
 * True when the draft is nothing but one web link (a LinkedIn profile, a
 * portfolio). The read never opens links, so the composer says so before
 * sending. A link inside real CV text is fine: the text is what gets read.
 */
export function isLinkOnlyResumeText(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed || /\s/.test(trimmed) || trimmed.includes('@')) return false;
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    // A real host has a dot and a top-level domain ("linkedin.com").
    return /\.[a-z]{2,}$/i.test(new URL(candidate).hostname);
  } catch {
    return false;
  }
}

/** Coarse buckets: message length is a shape, not a measurement. */
export function getLengthBucket(length: number) {
  if (length <= 0) return 'empty';
  if (length < 250) return 'short';
  if (length < 1500) return 'medium';
  return 'long';
}

export function getParsedCvAnalytics(parsed: ParsedCvData) {
  const record = parsed as Record<string, any>;
  const skillCount = Array.isArray(record.skills)
    ? record.skills.length
    : Array.isArray(record.skillGroups)
      ? record.skillGroups.reduce(
          (total: number, group: { items?: unknown[] }) =>
            total + (Array.isArray(group.items) ? group.items.length : 0),
          0,
        )
      : 0;

  return {
    hasTitle: Boolean(record.title || record.headline),
    hasSummary: Boolean(record.summary || record.description || record.about),
    experienceCount: Array.isArray(record.experience)
      ? record.experience.length
      : 0,
    educationCount: Array.isArray(record.education) ? record.education.length : 0,
    certificationCount: Array.isArray(record.certifications)
      ? record.certifications.length
      : 0,
    highlightCount: Array.isArray(record.highlights)
      ? record.highlights.length
      : 0,
    skillCount,
  };
}

export function useResumeImportComposer<TFile>({
  t,
  parse,
  describeFile,
  onParsed,
  parseFailedMessage,
  analyticsContext,
  onBeforeParse,
  onFileCleared,
  disabled,
  parseOnAttach,
  locked,
  onBusyChange,
  onDraftChange,
}: UseResumeImportComposerOptions<TFile>) {
  const [text, setText] = useState('');
  const [file, setFile] = useState<TFile | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasInput = text.trim().length > 0 || !!file;
  const isParsing = isSubmitting || Boolean(disabled);

  // Latest values for the imperative `submit`, which the owning screen can call
  // from its own button and which must not go stale between renders.
  const latest = useRef({ text, file, isParsing, locked });
  latest.current = { text, file, isParsing, locked };

  useEffect(() => {
    onBusyChange?.(isSubmitting);
  }, [isSubmitting, onBusyChange]);

  useEffect(() => {
    onDraftChange?.({ hasText: text.trim().length > 0, hasFile: !!file });
  }, [text, file, onDraftChange]);

  // A successful parse usually unmounts the composer (the owning step swaps it
  // for a "resume read" row), and an unmounted effect never gets to report that
  // the work finished - which left the owner's button spinning forever. Retract
  // both signals on the way out. The ref keeps this an unmount-only cleanup
  // rather than one that re-runs whenever a caller passes a new closure.
  const reportRef = useRef({ onBusyChange, onDraftChange });
  reportRef.current = { onBusyChange, onDraftChange };
  useEffect(
    () => () => {
      reportRef.current.onBusyChange?.(false);
      reportRef.current.onDraftChange?.({ hasText: false, hasFile: false });
    },
    [],
  );

  const submit = useCallback(
    async (override?: { file?: TFile | null }) => {
      const pendingFile =
        override && 'file' in override ? override.file! : latest.current.file;
      const pendingText = latest.current.text;
      const hasSomething = pendingText.trim().length > 0 || !!pendingFile;
      if (!hasSomething || latest.current.isParsing || latest.current.locked) return;

      setParseError(null);
      const source: 'file' | 'text' = pendingFile ? 'file' : 'text';
      const trimmedText = pendingText.trim();
      // The read cannot open links (nothing fetches them), so a pasted link
      // alone would only come back "incomplete" after a wasted read.
      if (!pendingFile && isLinkOnlyResumeText(trimmedText)) {
        setParseError(t('onboarding.cvJourney.linkUnsupported'));
        trackProductEvent('resume_import_parse_failed', {
          context: analyticsContext,
          source,
          durationMs: 0,
          errorCategory: 'validation',
          errorCode: 'LINK_ONLY',
          textLengthBucket: getLengthBucket(trimmedText.length),
        });
        return;
      }
      const validationError = !pendingFile
        ? getResumeTextValidationError(trimmedText)
        : null;
      if (validationError) {
        setParseError(
          t('onboarding.cvJourney.moreDetailRequired', {
            defaultValue: validationError,
          }),
        );
        trackProductEvent('resume_import_parse_failed', {
          context: analyticsContext,
          source,
          durationMs: 0,
          errorCategory: 'validation',
          errorCode: 'VALIDATION_ERROR',
          textLengthBucket: getLengthBucket(trimmedText.length),
        });
        return;
      }

      const described = pendingFile ? describeFile(pendingFile) : null;
      await onBeforeParse?.(source);

      const startedAt = Date.now();
      setIsSubmitting(true);
      trackProductEvent('resume_import_parse_started', {
        context: analyticsContext,
        source,
        textLengthBucket: getLengthBucket(trimmedText.length),
        fileMimeType: described?.type,
        fileSizeKb: described?.sizeKb ?? null,
      });

      try {
        const result = await parse({ file: pendingFile, text: trimmedText });
        if (!result?.parsed) throw new Error(parseFailedMessage);

        await onParsed({
          parsed: result.parsed,
          source,
          quality: result.quality,
          importEvidenceId: result.importEvidenceId,
        });
        trackProductEvent('resume_import_parse_completed', {
          context: analyticsContext,
          source,
          durationMs: Math.max(Date.now() - startedAt, 0),
          ...getParsedCvAnalytics(result.parsed),
        });
      } catch (error) {
        if (isCvImportReviewCancelled(error)) return;
        if (isCvImportSourceChanged(error)) {
          setParseError(t("seeker:cv.importEvidence.sourceChanged"));
          return;
        }
        // Recoverable outcomes (incident 2026-09-02): nothing was written. When
        // the server returned the exact text it read, place it in the editor so
        // the user can review it and extract again through the text path.
        const recovery = getCvParseRecovery(error);
        const reviewOffered = Boolean(
          recovery?.extractedText && source === 'file',
        );
        if (recovery?.extractedText && reviewOffered) {
          setFile(null);
          onFileCleared?.();
          setText(recovery.extractedText);
        }
        const message = recovery
          ? resolveCvParseRecoveryMessage(t, recovery, { reviewOffered })
          : (resolveImportedCvIncompleteMessage(t, error) ??
            // The server's own check for a pasted link (older drafts, or a
            // link the composer's check let through).
            (error instanceof OnboardingApiError && error.code === 'CV_LINK_UNSUPPORTED'
              ? t('onboarding.cvJourney.linkUnsupported')
              : isUnreadableResumeFileError(error)
              ? t('onboarding.cvJourney.fileTextUnreadable', {
                  defaultValue:
                    "We couldn't read enough text from this file. Try a text-based PDF or Word document, or paste the resume text.",
                })
              : error instanceof Error
                ? error.message
                : parseFailedMessage));
        setParseError(message);
        const errorCode =
          error instanceof OnboardingApiError ? error.code : undefined;
        trackProductEvent('resume_import_parse_failed', {
          context: analyticsContext,
          source,
          durationMs: Math.max(Date.now() - startedAt, 0),
          errorCode,
          ...getErrorObservabilityProperties(error),
        });
        if (!isExpectedOnboardingApiError(error)) {
          captureProductException(error, {
            feature: 'resume_import',
            action: 'parse',
            context: analyticsContext,
            source,
            errorCode,
          });
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      analyticsContext,
      describeFile,
      onBeforeParse,
      onFileCleared,
      onParsed,
      parse,
      parseFailedMessage,
      t,
    ],
  );

  /**
   * Record an attached file. With `parseOnAttach` the read starts straight
   * away, so the file is passed to `submit` explicitly rather than waiting a
   * render for the state to land.
   */
  const attachFile = useCallback(
    (selected: TFile, origin: 'picker' | 'drop' = 'picker') => {
      const described = describeFile(selected);
      setFile(selected);
      setParseError(null);
      trackProductEvent('resume_import_file_selected', {
        context: analyticsContext,
        fileMimeType: described.type || 'application/octet-stream',
        fileSizeKb: described.sizeKb,
        source: origin,
      });
      if (parseOnAttach) void submit({ file: selected });
    },
    [analyticsContext, describeFile, parseOnAttach, submit],
  );

  const clearFile = useCallback(() => {
    setFile(null);
    onFileCleared?.();
    trackProductEvent('resume_import_file_cleared', {
      context: analyticsContext,
    });
  }, [analyticsContext, onFileCleared]);

  return {
    text,
    setText,
    file,
    attachFile,
    clearFile,
    parseError,
    setParseError,
    isSubmitting,
    isParsing,
    hasInput,
    submit,
  };
}
